import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createMultimediaRequest } from "@services/multimedia/multimediaApi";
import {
  addWalkingRoute,
  appendModePointMedia,
  patchModePoint,
  removeModePoint,
  removeModePointMedia,
  removeWalkingRoute,
  resetModeToolDraft,
  setModeOptions,
  setModePointsForStartMode,
  setSelectedMode,
  setSelectedModeContext,
  setSelectedModePointId,
  setSelectedWalkingRouteKey,
  setWalkingRouteName,
  syncModePointsFromMap,
  updateModePointField,
} from "@store/slices/modeToolDraftSlice";
import { inferMediaType, resolveMediaUrl } from "@utils/media/mediaUrl";
import {
  MODE_ROUTE_COLOR_BY_TYPE,
  buildModeOptionsFromPoint,
} from "@utils/mode_tool/modeRoute";

const MODE_MARKER_COLORS = [
  "#f59e0b",
  "#22c55e",
  "#3b82f6",
  "#ec4899",
  "#14b8a6",
  "#eab308",
  "#6366f1",
  "#f97316",
  "#06b6d4",
  "#84cc16",
];

function getActiveRouteKey(mode, selectedRouteKey) {
  return String(mode || "") === "walking"
    ? String(selectedRouteKey || "")
    : "default";
}

// Coordinates Mode Tool state between map markers and redux draft.
function useModeRouteTool({
  isPointMode,
  draftPoints,
  mapPoints,
  selectedStartPoint,
  setIsDrawingEnabled,
  setRouteColor,
  setRouteDashArray,
  setMarkerVariant,
  setPointsFromList,
  onModePointCreated,
}) {
  const dispatch = useDispatch();
  const {
    selectedMode,
    selectedModePointId,
    modeOptions,
    modePointsByStartPoint,
    selectedWalkingRouteKey,
    walkingRouteKeysByOwner,
    walkingRouteMetaByOwner,
  } = useSelector((state) => state.modeToolDraft);
  // Tracks previous start-point context to detect hard context switches.
  const lastStartPointIdRef = useRef("");
  const lastMapPointCountRef = useRef(0);
  const lastMapPointIdsRef = useRef(new Set());
  const suppressNextAutoSelectRef = useRef(false);

  // Assigns a stable marker color per mode based on the current mode list order.
  function getModeMarkerColor(modeValue, options = modeOptions) {
    const list = Array.isArray(options) ? options : [];
    const index = list.findIndex(
      (mode) => String(mode?.value || "") === String(modeValue || ""),
    );
    if (index < 0) return MODE_MARKER_COLORS[0];
    return MODE_MARKER_COLORS[index % MODE_MARKER_COLORS.length];
  }

  function resetToolState() {
    // Full reset requested by UI (e.g., close Mode Tool).
    dispatch(resetModeToolDraft());
    setIsDrawingEnabled(false);
    setRouteColor("#38bdf8");
  }

  function handleSelectStartPoint(point) {
    // Entering/leaving a driving tour points resets mode selection,
    // then restores visible markers to base driving tour points (red).
    const nextId = point?.id || "";
    if (!nextId) {
      dispatch(setSelectedMode(""));
      dispatch(setSelectedModePointId(""));
      dispatch(
        setSelectedModeContext({
          ownerId: "",
          routeTypeId: "",
          mode: "",
          sourceFrom: "",
          sourceName: "",
        }),
      );
      dispatch(setModeOptions([]));
      setIsDrawingEnabled(false);
      setRouteColor("#38bdf8");
      setRouteDashArray([1, 0]);
      setPointsFromList(draftPoints || [], {
        markerColor: "#ef4444",
        markerVariant: "start-point",
      });
      return;
    }
    dispatch(setSelectedMode(""));
    dispatch(setSelectedModePointId(""));
    dispatch(
      setSelectedModeContext({
        ownerId: "",
        routeTypeId: "",
        mode: "",
        sourceFrom: "",
        sourceName: "",
      }),
    );
    dispatch(setModeOptions(buildModeOptionsFromPoint(point)));
    setIsDrawingEnabled(false);
    setRouteColor("#38bdf8");
    setRouteDashArray([1, 0]);
    setPointsFromList(draftPoints || [], {
      markerColor: "#ef4444",
      markerVariant: "start-point",
    });
  }

  function handleModeSelect(modeValue) {
    const value = String(modeValue || "");
    const startPointId = selectedStartPoint?.id;
    if (!value || !startPointId) return;

    if (selectedMode) {
      // Persist current mode geometry before switching to another mode.
      dispatch(
        setModePointsForStartMode({
          startPointId,
          mode: selectedMode,
          points: mapPoints,
          routeKey: getActiveRouteKey(selectedMode, selectedWalkingRouteKey),
        }),
      );
    }

    const activeRouteKey = getActiveRouteKey(value, selectedWalkingRouteKey);
    const pointsForMode = Array.isArray(
      modePointsByStartPoint?.[startPointId]?.[value]?.[activeRouteKey],
    )
      ? modePointsByStartPoint[startPointId][value][activeRouteKey]
      : [];
    const selectedOption = (modeOptions || []).find(
      (item) => String(item?.value || "") === value,
    );
    const markerColor = getModeMarkerColor(value);
    const markerVariant =
      value === "driving"
        ? "mode-driving"
        : value === "walking"
          ? "mode-walking"
          : value === "random"
            ? "mode-random"
            : "default";
    dispatch(setSelectedMode(value));
    dispatch(
      setSelectedModeContext({
        ownerId: selectedOption?.ownerId || selectedStartPoint?.id || "",
        routeTypeId: selectedOption?.routeTypeId || "",
        mode: value,
        sourceFrom: "start_point",
        sourceName:
          selectedStartPoint?.zoneName || selectedStartPoint?.id || "",
      }),
    );
    setIsDrawingEnabled(true);
    setMarkerVariant(markerVariant);
    // Avoid blank-map flicker/race while backend hydration for this mode is still loading.
    if (Array.isArray(pointsForMode) && pointsForMode.length) {
      suppressNextAutoSelectRef.current = true;
      setPointsFromList(pointsForMode, {
        markerColor: markerColor,
        markerVariant,
        startPointMarker:
          value === "driving" && selectedStartPoint ? selectedStartPoint : null,
        includeStartPointInBounds: value === "driving",
        autoFitBounds: true,
      });
      dispatch(setSelectedModePointId(""));
    }
    if (value === "driving") {
      setRouteColor("#4faede");
      setRouteDashArray([1, 0]);
    } else if (value === "walking") {
      setRouteColor("#588354");
      setRouteDashArray([2, 2]);
    } else {
      setRouteColor(MODE_ROUTE_COLOR_BY_TYPE[value] || "#e879f9");
      setRouteDashArray([1, 0]);
    }
  }

  useEffect(() => {
    const currentStartPointId = selectedStartPoint?.id || "";
    // Business rule: when startPoint.id changes, Mode Tool starts from zero.
    if (
      lastStartPointIdRef.current &&
      currentStartPointId &&
      lastStartPointIdRef.current !== currentStartPointId
    ) {
      dispatch(resetModeToolDraft());
      setIsDrawingEnabled(false);
      setRouteColor("#38bdf8");
      setRouteDashArray([1, 0]);
      setPointsFromList(draftPoints || [], {
        markerColor: "#ef4444",
        markerVariant: "start-point",
      });
    }
    lastStartPointIdRef.current = currentStartPointId;
  }, [
    dispatch,
    draftPoints,
    selectedStartPoint,
    setIsDrawingEnabled,
    setPointsFromList,
    setRouteColor,
    setRouteDashArray,
  ]);

  useEffect(() => {
    if (!isPointMode || !selectedMode || !selectedStartPoint?.id) return;
    if (
      selectedMode === "walking" &&
      !String(selectedWalkingRouteKey || "").trim()
    )
      return;
    // Keep redux in sync with marker moves/additions for the active mode.
    dispatch(
      syncModePointsFromMap({
        startPointId: selectedStartPoint.id,
        mode: selectedMode,
        mapPoints,
        routeKey: getActiveRouteKey(selectedMode, selectedWalkingRouteKey),
      }),
    );
    // Auto-selects the latest created point so editor opens immediately on creation.
    const nextCount = Array.isArray(mapPoints) ? mapPoints.length : 0;
    const prevCount = lastMapPointCountRef.current;
    if (suppressNextAutoSelectRef.current) {
      suppressNextAutoSelectRef.current = false;
    } else if (nextCount > prevCount) {
      const previousIds = lastMapPointIdsRef.current;
      const createdPoint =
        (Array.isArray(mapPoints) ? mapPoints : []).find(
          (point) => point?.id && !previousIds.has(String(point.id)),
        ) || mapPoints[nextCount - 1];
      if (createdPoint?.id) {
        dispatch(setSelectedModePointId(createdPoint.id));
        onModePointCreated?.(createdPoint);
      }
    }
    lastMapPointCountRef.current = nextCount;
    lastMapPointIdsRef.current = new Set(
      (Array.isArray(mapPoints) ? mapPoints : []).map((point) =>
        String(point?.id || ""),
      ),
    );
  }, [
    dispatch,
    isPointMode,
    mapPoints,
    selectedMode,
    selectedStartPoint,
    selectedWalkingRouteKey,
  ]);

  const activeModePoints = useMemo(
    () =>
      selectedStartPoint?.id && selectedMode
        ? modePointsByStartPoint?.[selectedStartPoint.id]?.[selectedMode]?.[
            getActiveRouteKey(selectedMode, selectedWalkingRouteKey)
          ] || []
        : [],
    [
      modePointsByStartPoint,
      selectedMode,
      selectedStartPoint,
      selectedWalkingRouteKey,
    ],
  );

  const modePointCount = useMemo(
    () =>
      (modeOptions || []).reduce((acc, mode) => {
        const key = mode.value;
        let count = 0;
        if (selectedStartPoint?.id && key === "walking") {
          const routeKeys = Array.isArray(
            walkingRouteKeysByOwner?.[selectedStartPoint.id],
          )
            ? walkingRouteKeysByOwner[selectedStartPoint.id]
            : [];
          count = routeKeys.reduce((sum, routeKey) => {
            const list =
              modePointsByStartPoint?.[selectedStartPoint.id]?.[key]?.[
                routeKey
              ];
            return sum + (Array.isArray(list) ? list.length : 0);
          }, 0);
        } else if (selectedStartPoint?.id) {
          count =
            modePointsByStartPoint?.[selectedStartPoint.id]?.[key]?.default
              ?.length || 0;
        }
        acc[key] = count;
        return acc;
      }, {}),
    [
      modeOptions,
      modePointsByStartPoint,
      selectedStartPoint,
      walkingRouteKeysByOwner,
    ],
  );

  useEffect(() => {
    if (!selectedStartPoint?.id || selectedMode !== "walking") return;
    setRouteColor("#588354");
    setRouteDashArray([2, 2]);
    const routeKey = getActiveRouteKey(selectedMode, selectedWalkingRouteKey);
    const pointsForRoute =
      modePointsByStartPoint?.[selectedStartPoint.id]?.walking?.[routeKey] ||
      [];
    const markerColor = getModeMarkerColor("walking");
    setMarkerVariant("mode-walking");
    suppressNextAutoSelectRef.current = true;
    setPointsFromList(pointsForRoute, {
      markerColor,
      markerVariant: "mode-walking",
      startPointMarker: null,
      autoFitBounds: true,
    });
    dispatch(setSelectedModePointId(""));
    // Intentionally tied only to route-context switches to avoid redux<->map feedback loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedMode,
    selectedStartPoint?.id,
    selectedWalkingRouteKey,
    setRouteColor,
    setRouteDashArray,
  ]);

  useEffect(() => {
    // Single-mode flow: auto-select the only mode so routeTypeId/context are always initialized.
    if (!selectedStartPoint?.id) return;
    if (!Array.isArray(modeOptions) || modeOptions.length !== 1) return;
    const onlyMode = String(modeOptions[0]?.value || "");
    if (!onlyMode) return;
    if (String(selectedMode || "") === onlyMode) return;
    handleModeSelect(onlyMode);
  }, [handleModeSelect, modeOptions, selectedMode, selectedStartPoint]);

  function updateActiveModePoint(pointId, field, value) {
    if (!selectedStartPoint?.id || !selectedMode || !pointId) return;
    dispatch(
      updateModePointField({
        startPointId: selectedStartPoint.id,
        mode: selectedMode,
        pointId,
        field,
        value,
        routeKey: getActiveRouteKey(selectedMode, selectedWalkingRouteKey),
      }),
    );
  }

  function getActiveModePointById(pointId) {
    if (!pointId) return null;
    const current =
      selectedStartPoint?.id && selectedMode
        ? modePointsByStartPoint?.[selectedStartPoint.id]?.[selectedMode]?.[
            getActiveRouteKey(selectedMode, selectedWalkingRouteKey)
          ] || []
        : [];
    return current.find((item) => item.id === pointId) || null;
  }

  function patchActiveModePoint(pointId, payload = {}) {
    if (!selectedStartPoint?.id || !selectedMode || !pointId) return;
    dispatch(
      patchModePoint({
        startPointId: selectedStartPoint.id,
        mode: selectedMode,
        pointId,
        payload,
        routeKey: getActiveRouteKey(selectedMode, selectedWalkingRouteKey),
      }),
    );
  }

  function commitActiveModePoint(pointId, pointPayload = {}) {
    if (!selectedStartPoint?.id || !selectedMode || !pointId) return;
    const currentPoint = getActiveModePointById(pointId) || {};
    const isExisting =
      Boolean(
        pointPayload?.isPersisted ||
        pointPayload?.internalId ||
        pointPayload?.serverId,
      ) ||
      Boolean(
        currentPoint?.isPersisted ||
        currentPoint?.internalId ||
        currentPoint?.serverId,
      ) ||
      !String(pointPayload?.id || currentPoint?.id || "").startsWith("point-");
    dispatch(
      patchModePoint({
        startPointId: selectedStartPoint.id,
        mode: selectedMode,
        pointId,
        routeKey: getActiveRouteKey(selectedMode, selectedWalkingRouteKey),
        payload: {
          ...(pointPayload || {}),
          // Lat/Lng are mandatory in mode-tool draft commits.
          latitude: pointPayload?.latitude ?? currentPoint?.latitude ?? 0,
          longitude: pointPayload?.longitude ?? currentPoint?.longitude ?? 0,
          isPersisted: Boolean(
            pointPayload?.isPersisted ?? currentPoint?.isPersisted,
          ),
          committedAs: isExisting ? "update" : "add",
        },
      }),
    );
  }

  async function handleUploadModePointMedia(pointId, files, extraMeta = {}) {
    if (
      !selectedStartPoint?.id ||
      !selectedMode ||
      !pointId ||
      !Array.isArray(files) ||
      !files.length
    )
      return;
    const uploaded = [];
    for (const file of files) {
      const response = await createMultimediaRequest({ file });
      const body = response?.body || {};
      const rawItem = Array.isArray(body?.multimediaList)
        ? body.multimediaList[0]
        : Array.isArray(body?.multimedia)
          ? body.multimedia[0]
          : body?.multimedia || null;
      if (!rawItem) continue;
      const rawUrl = rawItem?.url || rawItem?.mmd_url || "";
      uploaded.push({
        id: rawItem?.id || rawItem?.mmd_id || "",
        sourceId: rawItem?.id || rawItem?.mmd_id || "",
        url: resolveMediaUrl(rawUrl),
        mediaType:
          rawItem?.mediaType ||
          rawItem?.mmd_media_type ||
          inferMediaType(rawUrl),
        mimeType: rawItem?.mimeType || rawItem?.mmd_mime_type || "",
        language: String(
          extraMeta?.language || extraMeta?.languageIso || "",
        ).toLowerCase(),
        mediaRole: String(extraMeta?.mediaRole || ""),
      });
    }
    if (!uploaded.length) return;
    dispatch(
      appendModePointMedia({
        startPointId: selectedStartPoint.id,
        mode: selectedMode,
        pointId,
        media: uploaded,
        routeKey: getActiveRouteKey(selectedMode, selectedWalkingRouteKey),
      }),
    );
  }

  async function uploadModePointMediaFiles(files, extraMeta = {}) {
    if (!Array.isArray(files) || !files.length) return [];
    const uploaded = [];
    for (const file of files) {
      const response = await createMultimediaRequest({ file });
      const body = response?.body || {};
      const rawItem = Array.isArray(body?.multimediaList)
        ? body.multimediaList[0]
        : Array.isArray(body?.multimedia)
          ? body.multimedia[0]
          : body?.multimedia || null;
      if (!rawItem) continue;
      const rawUrl = rawItem?.url || rawItem?.mmd_url || "";
      uploaded.push({
        id: rawItem?.id || rawItem?.mmd_id || "",
        sourceId: rawItem?.id || rawItem?.mmd_id || "",
        url: resolveMediaUrl(rawUrl),
        mediaType:
          rawItem?.mediaType ||
          rawItem?.mmd_media_type ||
          inferMediaType(rawUrl),
        mimeType: rawItem?.mimeType || rawItem?.mmd_mime_type || "",
        language: String(
          extraMeta?.language || extraMeta?.languageIso || "",
        ).toLowerCase(),
        mediaRole: String(extraMeta?.mediaRole || ""),
      });
    }
    return uploaded;
  }

  function handleRemoveModePointMedia(pointId, mediaId) {
    if (!selectedStartPoint?.id || !selectedMode || !pointId || !mediaId)
      return;
    dispatch(
      removeModePointMedia({
        startPointId: selectedStartPoint.id,
        mode: selectedMode,
        pointId,
        mediaId,
        routeKey: getActiveRouteKey(selectedMode, selectedWalkingRouteKey),
      }),
    );
  }

  function removeActiveModePoint(pointId) {
    if (!selectedStartPoint?.id || !selectedMode || !pointId) return;
    dispatch(
      removeModePoint({
        startPointId: selectedStartPoint.id,
        mode: selectedMode,
        pointId,
        routeKey: getActiveRouteKey(selectedMode, selectedWalkingRouteKey),
      }),
    );
  }

  const activeWalkingRouteKeys = useMemo(
    () =>
      selectedStartPoint?.id && selectedMode === "walking"
        ? walkingRouteKeysByOwner?.[selectedStartPoint.id] || []
        : [],
    [selectedMode, selectedStartPoint, walkingRouteKeysByOwner],
  );

  const activeWalkingRouteMetaByKey = useMemo(
    () =>
      selectedStartPoint?.id && selectedMode === "walking"
        ? walkingRouteMetaByOwner?.[selectedStartPoint.id] || {}
        : {},
    [selectedMode, selectedStartPoint, walkingRouteMetaByOwner],
  );

  function selectWalkingRoute(routeKey) {
    dispatch(setSelectedWalkingRouteKey(String(routeKey || "")));
  }

  function createWalkingRoute() {
    const ownerId = String(selectedStartPoint?.id || "");
    if (!ownerId) return;
    dispatch(addWalkingRoute({ ownerId }));
  }

  function updateWalkingRouteName(routeKey, iso, value) {
    const ownerId = String(selectedStartPoint?.id || "");
    if (!ownerId || !routeKey || !iso) return;
    dispatch(
      setWalkingRouteName({
        ownerId,
        routeKey: String(routeKey),
        iso,
        value,
      }),
    );
  }

  function removeWalkingRouteLocal(routeKey) {
    const ownerId = String(selectedStartPoint?.id || "");
    if (!ownerId || !routeKey) return;
    dispatch(removeWalkingRoute({ ownerId, routeKey: String(routeKey) }));
  }

  return {
    selectedMode,
    selectedModePointId,
    modeOptions,
    activeModePoints,
    modePointCount,
    selectedWalkingRouteKey,
    activeWalkingRouteKeys,
    activeWalkingRouteMetaByKey,
    selectWalkingRoute,
    createWalkingRoute,
    updateWalkingRouteName,
    removeWalkingRouteLocal,
    suppressNextModePointAutoSelect: () => {
      suppressNextAutoSelectRef.current = true;
    },
    setSelectedModePointId: (id) => dispatch(setSelectedModePointId(id)),
    resetToolState,
    handleSelectStartPoint,
    handleModeSelect,
    updateActiveModePoint,
    getActiveModePointById,
    patchActiveModePoint,
    commitActiveModePoint,
    removeActiveModePoint,
    uploadModePointMediaFiles,
    handleUploadModePointMedia,
    handleRemoveModePointMedia,
  };
}

export default useModeRouteTool;
